import styled, { keyframes, css, createGlobalStyle } from 'styled-components';

// Keyframes
const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Main Wrapper
export const FlowBuilderContainer = styled.div`
  position: relative;
  height: 100vh;
  background: #f7f9fa;
  font-family:
    'Inter',
    -apple-system,
    BlinkMacSystemFont,
    sans-serif;
  overflow: hidden;
  user-select: none;

  @keyframes pulse {
    0%,
    100% {
      transform: scale(1);
      opacity: 1;
    }
    50% {
      transform: scale(1.08);
      opacity: 0.85;
    }
  }
`;

// ============ HEADER ============
export const Header = styled.header`
  position: absolute;
  top: 14px;
  left: 14px;
  right: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 24px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 50px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  z-index: 2;
  min-height: 54px;
  transition: all 0.350s ease-in-out;
  
  .trial-banner-visible & {
    top: 54px;
  }
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
`;

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: #f2f4fc;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #7f8594;
  transition: all 0.2s;
  flex-shrink: 0;

  &:hover {
    background: #e8eaf6;
    transform: scale(1.05);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const TitleSection = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const WorkflowTitle = styled.input`
  font-size: 15px;
  font-weight: 600;
  color: #1a1a2e;
  background: transparent;
  border: none;
  padding: 4px 6px;
  border-radius: 4px;
  min-width: 300px;
  transition: all 0.15s;

  &:hover {
    background: #f5f7fa;
  }

  &:focus {
    outline: none;
    background: #f5f7fa;
  }
`;

export const EditIcon = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  color: #b0b7c3;
  transition: all 0.15s;

  &:hover {
    color: #64748b;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ============ PROGRESS STEPPER ============
export const HeaderCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
`;

export const ProgressStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

export const StepItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 500;
  color: ${props => (props.$active ? '#1A1A2E' : '#B0B7C3')};

  .dot {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: ${props => (props.$active ? '#3B82F6' : '#CBD5E1')};
    position: relative;

    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 6px;
      height: 6px;
      background: var(--white);
      border-radius: 50%;
    }
  }
`;

export const StepLine = styled.div`
  width: 100px;
  height: 2px;
  background: #e2e8f0;
`;

// ============ HEADER ACTIONS ============
export const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end;
`;

export const SaveDraftButton = styled.button`
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #e6e7ec;
  border-radius: 50px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    color: #334155;
    border-color: #c1c3ca;
  }
`;

export const NextButton = styled.button`
  padding: 8px 28px;
  background: #3b82f6;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #2563eb;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  &:disabled {
    background: #94a3b8;
    cursor: not-allowed;
  }
`;

// ============ CANVAS ============
export const CanvasArea = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: radial-gradient(circle, #d5dae1 1.5px, transparent 1.5px);
  background-size: 20px 20px;
  background-color: var(--white);
  cursor: ${props => (props.$isPanning ? 'grabbing' : 'grab')};
`;

export const CanvasContent = styled.div`
  min-height: 100%;
  padding: 100px 40px 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  will-change: transform;
  transition: transform 0.05s linear;
`;

// ============ START NODE ============
export const StartNode = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 7px 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: #64748b;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  letter-spacing: 0.02em;

  svg {
    width: 13px;
    height: 13px;
    color: #94a3b8;
  }
`;

// ============ CONNECTOR LINE ============
export const ConnectorLine = styled.div`
  z-index:2;
  width: 2px;
  height: ${props => props.$height || '36px'};
  background: #cbd5e1;
  position: relative;

  &::after {
    content: '';
    position: absolute;
    bottom: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    background: #ffffff;
    border: 2px solid #cbd5e1;
    border-radius: 50%;
  }
`;

// Variant with pin at top only (node edge), no pin at bottom (plus edge)
export const ConnectorLineSimple = styled.div`
  z-index: 2;
  width: 2px;
  height: ${props => props.$height || '20px'};
  background: #cbd5e1;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: 50%;
    transform: translateX(-50%);
    width: 8px;
    height: 8px;
    background: #ffffff;
    border: 2px solid #cbd5e1;
    border-radius: 50%;
  }
`;

// ============ BRANCH (YES/NO) ============
export const BranchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

export const BranchSplit = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;

  /* Horizontal bars removed — replaced by curved connectors on each arm */
`;

export const BranchArm = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* Must fit: node card (300px) + analytics (~176px) while centered */
  min-width: 520px;
  padding: 0 20px;
  position: relative;

  /* Make the last vertical line stretch to fill remaining space
     so both arms end at the same level.
     margin-bottom leaves room for the curved bottom connector
     so the straight line doesn't poke past the curve. */
  & > ${() => BranchVerticalLine}:last-child {
    flex: 1;
    min-height: 16px;
    margin-bottom: 14px;
  }

  /* Hide the first vertical line — replaced by curved top connector */
  & > ${() => BranchVerticalLine}:first-child {
    background: transparent;
  }

  /* ── Left arm (Yes) ── */

  /* Top curve: ╭───  */
  &:first-child::before {
    content: '';
    position: absolute;
    top: 0;
    left: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-top: 2px solid #cbd5e1;
    border-left: 2px solid #cbd5e1;
    border-top-left-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }

  /* Bottom curve: ╰───  */
  &:first-child::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-bottom: 2px solid #cbd5e1;
    border-left: 2px solid #cbd5e1;
    border-bottom-left-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }

  /* ── Right arm (No) ── */

  /* Top curve: ───╮  */
  &:last-child::before {
    content: '';
    position: absolute;
    top: 0;
    right: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-top: 2px solid #cbd5e1;
    border-right: 2px solid #cbd5e1;
    border-top-right-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }

  /* Bottom curve: ───╯  */
  &:last-child::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-bottom: 2px solid #cbd5e1;
    border-right: 2px solid #cbd5e1;
    border-bottom-right-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }
`;

export const BranchLabel = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 3px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => props.$color || (props.$yes ? '#16A34A' : '#DC2626')};
  background: ${props => props.$bg || (props.$yes ? '#F0FDF4' : '#FEF2F2')};
  border: 1px solid ${props => props.$border || (props.$yes ? '#BBF7D0' : '#FECACA')};
  border-radius: 20px;
  padding: 4px 16px;
  z-index: 1;

  .branch-count {
    min-width: 18px;
    height: 18px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0 6px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 700;
    line-height: 1;
    background: linear-gradient(270deg, rgb(250, 22, 243) 3.85%, rgb(58, 125, 239) 96.15%) text;
    color: transparent;
    -webkit-text-fill-color: transparent;
  }
`;

/* ── 4-branch split (Check Email Status) ── */
export const BranchSplitFour = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
`;

export const BranchArmFour = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: ${props => props.$hasNodes ? '520px' : '200px'};
  padding: 0 20px;
  transition: min-width 0.2s ease;
  position: relative;

  & > ${() => BranchVerticalLine}:last-child {
    flex: 1;
    min-height: 16px;
    margin-bottom: 14px;
  }

  & > ${() => BranchVerticalLine}:first-child {
    background: transparent;
  }

  /* ── Leftmost arm: curved top-left + bottom-left connectors ── */
  &:first-child::before {
    content: '';
    position: absolute;
    top: 0;
    left: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-top: 2px solid #cbd5e1;
    border-left: 2px solid #cbd5e1;
    border-top-left-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }
  &:first-child::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-bottom: 2px solid #cbd5e1;
    border-left: 2px solid #cbd5e1;
    border-bottom-left-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }

  /* ── Rightmost arm: curved top-right + bottom-right connectors ── */
  &:last-child::before {
    content: '';
    position: absolute;
    top: 0;
    right: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-top: 2px solid #cbd5e1;
    border-right: 2px solid #cbd5e1;
    border-top-right-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }
  &:last-child::after {
    content: '';
    position: absolute;
    bottom: 0;
    right: calc(50% - 1px);
    width: calc(50% + 1px);
    height: 16px;
    border-bottom: 2px solid #cbd5e1;
    border-right: 2px solid #cbd5e1;
    border-bottom-right-radius: 14px;
    pointer-events: none;
    z-index: 1;
  }

  /* ── Middle arms: full-width horizontal bar + center vertical connector ── */
  &:not(:first-child):not(:last-child)::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 16px;
    border-top: 2px solid #cbd5e1;
    background: linear-gradient(
      to right,
      transparent calc(50% - 1px),
      #cbd5e1 calc(50% - 1px),
      #cbd5e1 calc(50% + 1px),
      transparent calc(50% + 1px)
    );
    pointer-events: none;
    z-index: 1;
  }
  &:not(:first-child):not(:last-child)::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    height: 16px;
    border-bottom: 2px solid #cbd5e1;
    background: linear-gradient(
      to right,
      transparent calc(50% - 1px),
      #cbd5e1 calc(50% - 1px),
      #cbd5e1 calc(50% + 1px),
      transparent calc(50% + 1px)
    );
    pointer-events: none;
    z-index: 1;
  }
`;

export const BranchVerticalLine = styled.div`
  width: 2px;
  height: ${props => props.$height || '16px'};
  background: #cbd5e1;
  border-radius: 4px;
  flex-shrink: 0;
`;

// ============ FLOW NODE CARD ============
export const FlowNodeCard = styled.div`
  width: 300px;
  background: var(--white);
  border-radius: 14px;
  box-shadow: ${props =>
    props.$selected
      ? '0 0 0 3px rgba(59, 130, 246, 0.08), 0 4px 12px rgba(0, 0, 0, 0.08)'
      : '0 1px 4px rgba(0, 0, 0, 0.04)'};
  transition: all 0.2s;
  cursor: pointer;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: -1px;
    background: ${props =>
    props.$selected ? 'linear-gradient(90deg, #367EEE 0%, #B74BFF 50.96%, #FEB508 100%)' : '#E2E8F0'};
    border-radius: 14px;
    z-index: -1;
  }

  &:hover {
    border-color: ${props => (props.$selected ? '#3B82F6' : '#C1C7D0')};
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`;

export const FlowNodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
`;

export const FlowNodeIcon = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$bgColor || '#F1F5F9'};
  border-radius: 8px;
  color: ${props => props.$color || '#64748B'};
  flex-shrink: 0;

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const FlowNodeTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a2e;
`;

export const FlowNodeBody = styled.div`
  padding: 0 16px 14px;
`;

export const SelectDropdown = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 13px;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #cbd5e1;
  }

  svg {
    width: 20px;
    height: 20px;
    color: #94a3b8;
  }
`;

// ============ ADD NODE BUTTON ============
export const AddNodeButton = styled.button`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  border-radius: 5px;
  cursor: pointer;
  background: #317bff;
  padding: 0;
  transition: all 0.3s ease;
  z-index: 5;

  &:hover {
    transform: scale(1.1);
  }

  svg {
    width: 24px;
    height: 24px;
    transition: transform 0.3s ease;
  }
`;

// ============ END NODE ============
export const EndNodeCard = styled.div`
  width: 300px;
  background: #ffffff;
  border: 1.5px solid #e2e8f0;
  border-radius: 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
  overflow: hidden;
`;

export const EndNodeHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  border-bottom: none;

  .indicator {
    width: 10px;
    height: 10px;
    background: #86efac;
    border-radius: 50%;
  }

  span {
    font-size: 14px;
    font-weight: 600;
    color: #1a1a2e;
  }
`;

export const EndNodeBody = styled.div`
  padding: 10px 14px 14px;
`;

export const StatusTable = styled.table`
  width: 100%;
  border-spacing: 0;
  border: 1px solid #e8eaed;
  border-radius: 10px;
  overflow: hidden;

  th,
  td {
    padding: 4px 16px;
    text-align: left;
    font-size: 12px;
    border-bottom: 1px solid #e8eaed;
    border-right: 1px solid #e8eaed;
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  tr:last-child td {
    border-bottom: none;
  }

  th {
    color: #1a1a2e;
    font-weight: 600;
    font-size: 12px;
    background: transparent;
  }

  td {
    color: #1e293b;
  }

  .status-completed {
    color: #22c55e;
    font-weight: 500;
  }

  .status-failed {
    color: #ef4444;
    font-weight: 500;
  }

  .status-progress {
    color: #3b82f6;
    font-weight: 500;
  }

  .count {
    font-weight: 700;
  }

  tr:has(.status-completed) .count {
    color: #22c55e;
  }

  tr:has(.status-failed) .count {
    color: #ef4444;
  }

  tr:has(.status-progress) .count {
    color: #3b82f6;
  }

  tr[data-clickable] {
    cursor: pointer;
    transition: background 0.12s;
  }

  tr[data-clickable]:hover {
    background: #f8fafc;
  }

  tr[data-clickable]:has(.status-completed):hover {
    background: #f0fdf4;
  }

  tr[data-clickable]:has(.status-failed):hover {
    background: #fef2f2;
  }

  tr[data-clickable]:has(.status-progress):hover {
    background: #eff6ff;
  }

  tr[data-clickable] .count::after {
    content: ' →';
    font-size: 10px;
    opacity: 0.4;
    transition: opacity 0.12s;
  }

  tr[data-clickable]:hover .count::after {
    opacity: 1;
  }
`;

// ============ BOTTOM ACTIONS PANEL ============
export const ActionsPanel = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  width: fit-content;
  max-width: calc(100vw - 32px);
  margin-inline: auto;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 16px 16px 0 0;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
  z-index: 100;
  min-height: 320px;
  max-height: ${props => (props.$open ? '400px' : '0px')};
  padding: ${props => (props.$open ? '12px 16px' : '0 16px')};
  overflow: ${props => (props.$open ? 'visible' : 'hidden')};
  opacity: ${props => (props.$open ? '1' : '0')};
  transition:
    max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    padding 0.4s cubic-bezier(0.4, 0, 0.2, 1),
    opacity 0.3s ease;
  pointer-events: ${props => (props.$open ? 'auto' : 'none')};
`;

export const ActionsTabs = styled.div`
  display: inline-flex;
  align-items: center;
  background: #f1f3f6;
  border-radius: 10px;
  padding: 2px;
  gap: 2px;
  margin-bottom: 14px;
`;

export const ActionsTab = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  border: none;
  border-radius: 8px;
  padding: 7px 30px;
  background: ${props => (props.$active ? '#ffffff' : 'transparent')};
  color: ${props => (props.$active ? '#111827' : '#6b7280')};
  font-size: 14px;
  font-weight: ${props => (props.$active ? 500 : 400)};
  cursor: pointer;
  box-shadow: ${props => (props.$active ? '0 1px 3px rgba(15, 23, 42, 0.15)' : 'none')};
  transition: all 0.15s ease;

  &:hover {
    color: #111827;
  }

  svg {
    width: 15px;
    height: 15px;
    color: ${props => (props.$active ? '#3b82f6' : '#6b7280')};
  }
`;

export const ActionsGrid = styled.div`
  height: 247px;
  display: grid;
  grid-template-columns: repeat(5, 150px);
  gap: 12px;
  padding: 5px 5px 0 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

export const ActionTooltipSlot = styled.span`
  position: absolute;
  right: 0;
  top: 50%;
  width: 0;
  height: 0;
  overflow: visible;
  pointer-events: none;
`;

export const ActionDisabledTooltip = styled.span`
  position: fixed;
  display: block;
  width: max-content;
  max-width: min(280px, calc(100vw - 32px));
  padding: 8px 10px;
  font-size: 12px;
  line-height: 1.45;
  font-weight: 500;
  text-align: left;
  white-space: normal;
  overflow-wrap: anywhere;
  background: var(--blue);
  color: var(--white);
  border-radius: 8px;
  opacity: ${props => (props.$visible ? 1 : 0)};
  visibility: ${props => (props.$visible ? 'visible' : 'hidden')};
  transition: opacity 0.3s ease, visibility 0.3s ease, transform 0.3s ease;
  transform: ${props => (props.$visible ? 'translate(0, -50%)' : 'translate(10px, -50%)')};
  z-index: 999;
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: -10px;
    transform: translateY(-50%);
    width: 0;
    height: 0;
    border-top: 5px solid transparent;
    border-right: 10px solid var(--blue);
    border-bottom: 5px solid transparent;
  }
`;

export const ActionCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 150px;
  height: 115px;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  cursor: ${props => (props.$locked || props.$disabled ? 'not-allowed' : 'grab')};
  transition: all 0.2s;
  user-select: none;

  &:hover {
    border-color: #c1c7d0;
    background: #fafbfc;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &:active {
    cursor: ${props => (props.$locked ? 'pointer' : props.$disabled ? 'not-allowed' : 'grabbing')};
    transform: ${props => (props.$locked ? 'translateY(-1px)' : props.$disabled ? 'none' : 'scale(0.97)')};
  }

  &:hover .upgrade-cta {
    opacity: 1;
    transform: translate(-50%, 50%);
    pointer-events: auto;
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      background: #f8fafc;
      border: 1px dashed #cbd5e1;
      box-shadow: none;

      &:hover,
      &:active {
        border-color: #cbd5e1;
        background: #f8fafc;
        transform: none;
        box-shadow: none;
      }
    `}
`;

export const ActionDisabledBadge = styled.span`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  color: #64748b;
  pointer-events: none;

  svg {
    width: 12px;
    height: 12px;
  }
`;

export const ActionIconWrapper = styled.div`
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border-radius: 0;
  color: ${props => props.$color || '#64748B'};
  opacity: ${props => (props.$muted ? 0.55 : 1)};

  svg {
    width: 24px;
    height: 24px;
  }

  svg.gradient-icon,
  .gradient-icon svg {
    fill: url(#houdiny-icon-gradient);
    color: transparent;
  }

  svg.gradient-icon path,
  .gradient-icon svg path {
    fill: url(#houdiny-icon-gradient);
  }
`;

export const ActionLabel = styled.span`
  font-size: 11px;
  font-weight: 500;
  color: ${props => (props.$muted ? '#94a3b8' : '#475569')};
  text-align: center;
  line-height: 1.3;
`;

export const ActionLockBadge = styled.span`
  position: absolute;
  top: 10px;
  right: 10px;
  color: #9ca3af;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ActionUpgradeButton = styled.button`
  position: absolute;
  bottom: 50%;
  left: 50%;
  width: 100%;
  max-width: 100px;
  transform: translateX(-50%) translateY(6px);
  border: none;
  border-radius: 999px;
  padding: 8px 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: #3b82f6;
  color: #ffffff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  box-shadow: 0 6px 14px rgba(59, 130, 246, 0.3);
  opacity: 0;
  pointer-events: none;
  transition: all 0.18s ease;

  svg {
    width: 12px;
    height: 12px;
    color: #ffffff;
  }
`;

// ============ ZOOM PANEL ============
export const ZoomPanel = styled.div`
  position: absolute;
  bottom: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  gap: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 24px;
  padding: 6px 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  z-index: 50;
`;

export const ZoomButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  color: #3b82f6;
  transition: all 0.15s;
  flex-shrink: 0;

  &:hover {
    background: rgba(59, 130, 246, 0.08);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const ZoomTrack = styled.div`
  position: relative;
  width: 80px;
  height: 2px;
  background: #e2e8f0;
  margin: 0 4px;
  border-radius: 1px;
`;

export const ZoomIndicator = styled.div`
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background: #ffffff;
  border: 2px solid #3b82f6;
  border-radius: 50%;
  transition: left 0.15s ease;
`;

// ============ TOAST ============
export const ToastContainer = styled.div`
  position: fixed;
  bottom: 100px;
  right: 24px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 1000;
`;

export const Toast = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 18px;
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.12);
  animation: ${slideUp} 0.3s ease;
  min-width: 280px;

  ${props =>
    props.$type === 'success' &&
    css`
      border-left: 4px solid #22c55e;
    `}
  ${props =>
    props.$type === 'error' &&
    css`
      border-left: 4px solid #ef4444;
    `}
  ${props =>
    props.$type === 'warning' &&
    css`
      border-left: 4px solid #f59e0b;
    `}
  ${props =>
    props.$type === 'info' &&
    css`
      border-left: 4px solid #3b82f6;
    `}
`;

export const ToastContent = styled.div`
  flex: 1;

  .title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 2px;
  }

  .message {
    font-size: 13px;
    color: #64748b;
  }
`;

export const ToastClose = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #94a3b8;
  transition: all 0.15s;

  &:hover {
    background: #f1f5f9;
    color: #64748b;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

// ============ PROPERTIES PANEL ============
export const PropertiesPanel = styled.div`
  position: fixed;
  top: 90px;
  right: 20px;
  bottom: 20px;
  width: 370px;
  background: #ffffff;
  border-left: 1px solid #eaecf0;
  box-shadow: -4px 0 16px rgba(0, 0, 0, 0.06);
  transform: ${props => (props.$open ? 'translateX(0)' : 'translateX(110%)')};
  transition: transform 0.3s ease;
  border-radius: 20px;
  z-index: 200;
  display: flex;
  flex-direction: column;
`;

export const PropertiesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 20px;
  border-bottom: 1px solid #eaecf0;

  h3 {
    font-size: 15px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
  }
`;

export const PropertiesBody = styled.div`
  flex: 1;
  min-width: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  box-sizing: border-box;
`;

export const FormGroup = styled.div`
  margin-bottom: 20px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;

  label {
    display: block;
    font-size: 12px;
    font-weight: 500;
    color: #64748b;
    margin-bottom: 6px;
  }
`;

export const FormInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  transition: all 0.15s;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const FormTextarea = styled.textarea`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: #1e293b;
  transition: all 0.15s;
  min-height: 100px;
  resize: vertical;
  font-family: inherit;
  resize: none;

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
  }

  &::placeholder {
    color: #94a3b8;
  }
`;

export const InsertVariableWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

export const InsertVariableButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    background: #f0f4ff;
    border-color: #3b82f6;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const VariablesDropdown = styled.div`
  min-width: 270px;
  position: absolute;
  top: 35px;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 10;
  padding: 6px;
  animation: ${fadeIn} 0.15s ease;
`;

export const VariableItem = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 10px;
  background: transparent;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  color: #1e293b;
  cursor: pointer;
  text-align: left;
  transition: all 0.12s;

  &:hover {
    background: #f0f4ff;
    color: #3b82f6;
  }

  .label {
    display: block;
  }

  .value {
    font-size: 11px;
    color: #64748b;
    background: #f1f5f9;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: 'SF Mono', 'Fira Code', monospace;
    margin-left: auto;
  }
`;

export const CharCounter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
  font-size: 11px;
  font-weight: 500;
  color: ${props => (props.$over ? '#EF4444' : '#94A3B8')};
  transition: color 0.15s;
`;

export const CheckboxRow = styled.label`
  display: flex;
  align-items: flex-start;
  padding: 12px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  margin-top: 4px;
  opacity: ${props => (props.$dim ? 0.4 : 1)};

  &:hover {
    background: #f0f4ff;
    border-color: #c7d2fe;
  }

  /* Hide the real checkbox */
  input[type='checkbox'] {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
    pointer-events: none;
  }

  /* Custom visual checkbox drawn on the span's ::before */
  span {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    font-size: 13px;
    color: #334155;
    line-height: 18px;
    user-select: none;

    &::before {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      min-width: 16px;
      margin-top: 1px;
      border: 2px solid #cbd5e1;
      border-radius: 4px;
      background: #ffffff;
      transition: all 0.15s;
      box-sizing: border-box;
    }
  }

  /* Checked state */
  input[type='checkbox']:checked + span::before {
    background: #3b82f6;
    border-color: #3b82f6;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M2.5 6L5 8.5L9.5 3.5' stroke='white' stroke-width='1.8' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
    background-size: 12px 12px;
    background-position: center;
    background-repeat: no-repeat;
  }
`;

export const PropertiesFooter = styled.div`
  display: flex;
  gap: 10px;
  padding: 16px 20px;
  position: relative;
`;

export const DeleteButton = styled.button`
  width: 100%;
  padding: 10px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
  color: #dc2626;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.15s;

  &:hover {
    background: #fee2e2;
    border-color: #fca5a5;
  }

  svg {
    width: 15px;
    height: 15px;
  }
`;

export const DeleteConfirmTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 10;
  min-width: ${({ $wide }) => ($wide ? '280px' : '220px')};
  max-width: 320px;
  text-align: center;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 6px solid transparent;
    border-top-color: #ffffff;
  }

  &::before {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 7px solid transparent;
    border-top-color: #e2e8f0;
  }

  p {
    font-size: 13px;
    font-weight: 500;
    color: #334155;
    margin: 0 0 12px;
    line-height: 1.45;
  }

  .confirm-actions {
    display: flex;
    gap: 8px;
    justify-content: center;
  }
`;

export const ConfirmBtn = styled.button`
  padding: 6px 18px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid ${props => (props.$danger ? '#FECACA' : '#E2E8F0')};
  background: ${props => (props.$danger ? '#FEF2F2' : '#FFFFFF')};
  color: ${props => (props.$danger ? '#DC2626' : '#64748B')};

  &:hover {
    background: ${props => (props.$danger ? '#FEE2E2' : '#F8FAFC')};
  }
`;

export const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.15s;

  &:hover {
    background: #f1f5f9;
    color: #334155;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ============ LAUNCH PAGE ============
export const LaunchContainer = styled.div`
  position: absolute;
  top: 90px;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  gap: 32px;
  padding: 32px 40px;
  overflow: hidden;
  background: #f7f9fa;
`;

export const LaunchLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  min-width: 0;
  overflow-y: auto;
  padding-right: 8px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d5dd;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #98a2b3;
  }
`;

export const LaunchCard = styled.div`
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  padding: 32px;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
`;

export const LaunchRight = styled.div`
  width: 380px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const LaunchRightCard = styled.div`
  background: #ffffff;
  border: 1px solid #eaecf0;
  border-radius: 16px;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  overflow-y: auto;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #d0d5dd;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #98a2b3;
  }
`;

export const LaunchSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #101828;
  margin: 0;
  line-height: 1.4;
`;

export const LaunchSectionSubtitle = styled.p`
  font-size: 13px;
  color: #667085;
  margin: 0 0 12px;
  line-height: 1.5;
`;

export const LaunchFormGroup = styled.div`
  margin-bottom: 24px;

  &.starting-date-group {
    margin-bottom: 0;
  }

  > label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #344054;
    margin-bottom: 8px;

    .required {
      color: #ef4444;
      margin-left: 4px;
    }
  }
`;

export const LaunchInput = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  color: #101828;
  background: #ffffff;
  outline: none;
  transition: all 0.15s;
  box-sizing: border-box;

  &::placeholder {
    color: #98a2b3;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.08);
  }

  &:hover:not(:focus) {
    border-color: #98a2b3;
  }
`;

export const LaunchSelect = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  color: #344054;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s;

  &:hover {
    border-color: #98a2b3;
  }

  svg {
    width: 16px;
    height: 16px;
    color: #667085;
  }
`;

export const ScheduleToggle = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  padding: 20px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 12px;

  .toggle-left {
    h4 {
      font-size: 14px;
      font-weight: 600;
      color: #101828;
      margin: 0 0 4px;
    }
    p {
      font-size: 12px;
      color: #667085;
      margin: 0;
      line-height: 1.4;
    }
  }

  .toggle-buttons {
    display: flex;
    gap: 0;
    border: 1px solid #d0d5dd;
    border-radius: 8px;
    overflow: hidden;
    background: #ffffff;
  }
`;

export const ToggleOption = styled.button`
  padding: 8px 20px;
  font-size: 13px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.15s;
  background: ${props => (props.$active ? '#3B82F6' : '#FFFFFF')};
  color: ${props => (props.$active ? '#FFFFFF' : '#344054')};

  &:hover {
    background: ${props => (props.$active ? '#2563EB' : '#F9FAFB')};
  }

  &:first-child {
    border-right: 1px solid #d0d5dd;
  }
`;

export const TimingRow = styled.div`
  display: flex;
  gap: 12px;

  > div {
    flex: 1;
  }
`;

export const TimingSelect = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  color: #344054;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s;

  .label {
    color: #667085;
    font-weight: 500;
    font-size: 13px;
  }

  .value {
    color: #101828;
    flex: 1;
    text-align: right;
    font-weight: 500;
  }

  svg {
    width: 14px;
    height: 14px;
    color: #667085;
  }

  &:hover {
    border-color: #98a2b3;
  }
`;

export const DaysRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 8px;
`;

export const DayCheckbox = styled.label`
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
  background: #f9fafb;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  transition: all 0.15s;
  white-space: nowrap;
  height: 40px;
  box-sizing: border-box;

  &:hover {
    background: #f2f4f7;
    border-color: #98a2b3;
  }

  input[type='checkbox'] {
    appearance: none;
    -webkit-appearance: none;
    min-width: 18px;
    min-height: 18px;
    width: 18px;
    height: 18px;
    border: 1.5px solid #d0d5dd;
    border-radius: 4px;
    background: #fff;
    cursor: pointer;
    margin: 0 12px 0 0;
    flex-shrink: 0;
    position: relative;

    &:checked {
      background: #3b82f6;
      border-color: #3b82f6;
    }

    &:checked::after {
      content: '✓';
      color: #fff;
      font-size: 12px;
      font-weight: 700;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }

  span {
    user-select: none;
    line-height: 1;
    display: flex;
    align-items: center;
  }

  input:checked + span {
    color: #101828;
    font-weight: 600;
  }
`;

export const BottomRow = styled.div`
  display: flex;
  gap: 16px;

  > div {
    flex: 1;
  }
`;

export const ProfileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px;
  background: #f9fafb;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  transition: all 0.15s;

  &:hover {
    background: #f2f4f7;
    border-color: #d0d5dd;
  }

  .avatar {
    width: 44px;
    height: 44px;
    border-radius: 50%;
    background: #e2e8f0;
    position: relative;
    overflow: visible;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    img {
      width: 100%;
      height: 100%;
      border-radius: 50%;
      object-fit: cover;
    }

    .linkedin-badge {
      position: absolute;
      bottom: -2px;
      right: -2px;
      width: 18px;
      height: 18px;
      background: #0076b2;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      font-size: 10px;
      font-weight: 700;
      font-family: Arial, sans-serif;
      border: 2px solid white;
    }
  }

  .name {
    flex: 1;
    font-size: 14px;
    font-weight: 500;
    color: #101828;
  }
`;

/** Matches AI campaign launch (Step8) `.col.autopilot` styling — fits narrow panels */
export const AutopilotCol = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding: 12px 14px;
  margin: 16px 0 0;
  border: 1px solid #efefef;
  border-radius: 10px;
  background: var(--white);
  position: relative;
  overflow: hidden;

  @media (min-width: 1200px) {
    padding: 16px 18px;
    gap: 16px;
  }

  .copy {
    flex: 1;
    min-width: 0;
  }

  .heading {
    display: block;
    font-size: 15px;
    line-height: 20px;
    font-weight: 600;
    margin: 0 0 4px;
    background: linear-gradient(90deg, #317bff 0%, #b74bff 50.96%, #fdb409 100%);
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  .text {
    display: block;
    font-size: 13px;
    line-height: 17px;
    font-weight: 300;
    overflow-wrap: break-word;
    word-break: break-word;
    background: linear-gradient(90deg, #317bff 0%, #b74bff 50.96%, #fdb409 100%);
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    padding: 2px;
    border-radius: 10px;
    background: linear-gradient(90deg, #317bff 0%, #b74bff 50.96%, #fdb409 100%);
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }

  .switch-holder {
    flex-shrink: 0;
    position: relative;
    z-index: 1;
    margin-left: 4px;
  }
`;

export const ToggleSwitch = styled.button`
  width: 44px;
  height: 24px;
  border-radius: 12px;
  border: none;
  cursor: pointer;
  position: relative;
  transition: background 0.2s;
  background: ${props => (props.$active ? '#3B82F6' : '#D0D5DD')};
  flex-shrink: 0;
  padding: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => (props.$active ? '22px' : '2px')};
    width: 20px;
    height: 20px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.15);
  }

  &:hover {
    background: ${props => (props.$active ? '#2563EB' : '#98A2B3')};
  }
`;

export const ProfilesLoading = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px 24px;
  color: #667085;
  font-size: 13px;
  background: #f9fafb;
  border-radius: 10px;
`;

export const ConnectLinkedInButton = styled.button`
  margin-top: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 12px 20px;
  background: #ffffff;
  border: 1.5px solid #3b82f6;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 600;
  color: #3b82f6;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #eff4ff;
    border-color: #3b82f6;
  }

  .li-icon {
    width: 20px;
    height: 20px;
    background: #0076b2;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 11px;
    font-weight: 700;
    font-family: Arial, sans-serif;
  }
`;

export const LaunchButton = styled.button`
  padding: 8px 28px;
  background: #3b82f6;
  border: none;
  border-radius: 20px;
  font-size: 13px;
  font-weight: 600;
  color: white;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: #2563eb;
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  ${props =>
    props.$disabledLook &&
    css`
      opacity: 0.5;
      cursor: not-allowed;
    `}
`;

export const ClickableStepItem = styled(StepItem)`
  cursor: pointer;
`;

export const AddLeadsButton = styled(SaveDraftButton)`
  background: rgba(59, 130, 246, 0.15);
  color: #3b82f6;
  border-color: rgba(59, 130, 246, 0.3);
`;

export const LeadsModalBody = styled.div`
  height: calc(100vh - 200px);
  max-height: 700px;
  min-height: 350px;
  overflow: hidden;

  .table-footer{
    left:0 !important;
  }
`;

export const CanvasTransformContent = styled(CanvasContent)`
  transform: translate(${props => props.$panX}px, ${props => props.$panY}px) scale(${props => props.$zoom});
  transform-origin: center top;
`;

export const LeadListText = styled.span`
  color: ${props => (props.$selected ? '#1A1A2E' : 'inherit')};
`;

export const ZoomIndicatorDynamic = styled(ZoomIndicator)`
  left: ${props => `${props.$left}%`};
`;

// ============ NODE ROW (card-width box; widget anchored to card's right edge) ============
export const NodeRow = styled.div`
  width: 300px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
`;

// ============ ANALYTICS WIDGET ============
export const AnalyticsWidgetWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  position: absolute;
  top: 10px;
  left: 100%;
  z-index: 20;
  pointer-events: none;
`;

export const AnalyticsConnector = styled.div`
  width: 16px;
  height: 2px;
  background: #cbd5e1;
  margin-top: 22px;
  flex-shrink: 0;
`;

export const AnalyticsCard = styled.div`
  width: 160px;
  background: #ffffff;
  border: 1px solid #e8eaed;
  border-radius: 12px;
  padding: 10px 14px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.04);
`;

export const AnalyticsTitle = styled.div`
  font-size: 10px;
  font-weight: 600;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 8px;
`;

export const AnalyticsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 0;

  & + & {
    border-top: 1px solid #f1f5f9;
  }
`;

export const AnalyticsLabel = styled.span`
  font-size: 11px;
  color: #64748b;
  font-weight: 500;
`;

export const AnalyticsValue = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: ${props => props.$color || '#1e293b'};
`;

export const AnalyticsRate = styled.span`
  font-size: 10px;
  font-weight: 500;
  color: ${props => props.$color || '#94a3b8'};
  margin-left: 4px;
  opacity: 0.8;
`;

export const FlowNodeDelayMeta = styled.span`
  font-weight: 400;
  font-size: 12px;
  opacity: 0.7;
`;

export const FlowNodeMessagePreview = styled.p`
  margin: 0;
  font-size: 13px;
  color: #64748b;
`;

export const NodeTypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const NodeTypeLabel = styled.span`
  font-weight: 500;
  color: #1e293b;
`;

export const MessageHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
`;

export const HeaderLabel = styled.label`
  margin: 0;
`;

export const AltMessageHint = styled.span`
  font-size: 11px;
  color: #94a3b8;
  font-weight: 400;
`;

export const OfferCreateLinkRow = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 6px;
`;

export const CreateOfferLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background: none;
  border: none;
  padding: 0;
  color: #94a3b8;
  cursor: pointer;
  font-size: 11px;
  font-weight: 400;
  text-decoration: none;

  &:hover {
    color: #64748b;
  }

  svg {
    width: 12px;
    height: 12px;
    flex-shrink: 0;
  }
`;

export const DelayRow = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const DelayInput = styled(FormInput)`
  flex: 1;
`;

export const DelayUnitSelect = styled.select`
  padding: 8px 12px;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 13px;
  outline: none;
  cursor: pointer;
  min-width: 100px;
`;

export const VoiceRecordedContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const VoiceRecordedRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 10px;
  border: 1px solid #74db9a;
  padding: 10px 12px;
`;

export const VoicePlayButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #22c55e;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const HiddenAudio = styled.audio`
  display: none;
`;

export const VoiceMeta = styled.div`
  flex: 1;
  min-width: 0;
`;

export const VoiceWaveTrack = styled.div`
  height: 4px;
  background: #bbf7d0;
  border-radius: 2px;
  overflow: hidden;
`;

export const VoiceWaveFill = styled.div`
  width: 100%;
  height: 100%;
  background: #22c55e;
  border-radius: 2px;
`;

export const VoiceFilename = styled.span`
  font-size: 11px;
  color: #15803d;
  margin-top: 4px;
  display: block;
`;

export const VoiceRemoveButton = styled.button`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: rgba(220, 38, 38, 0.1);
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

export const RecordingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 14px;
  border-radius: 12px;
  border: 1px solid rgba(239, 68, 68, 0.15);
`;

export const RecordingPulseOuter = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
  animation: pulse 1.5s ease-in-out infinite;
`;

export const RecordingPulseInner = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const RecordingText = styled.div`
  text-align: center;
`;

export const RecordingTime = styled.div`
  font-size: 24px;
  font-weight: 600;
  color: #ef4444;
  font-variant-numeric: tabular-nums;
`;

export const RecordingSubtext = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
`;

export const RecordingActions = styled.div`
  display: flex;
  gap: 10px;
`;

export const RecordingCancelButton = styled.button`
  padding: 8px 18px;
  border-radius: 8px;
  background: #f1f5f9;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
`;

export const RecordingStopButton = styled.button`
  padding: 8px 18px;
  border-radius: 8px;
  background: #ef4444;
  border: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  display: flex;
  align-items: center;
  gap: 6px;
`;

export const StartRecordButton = styled.button`
  width: 100%;
  padding: 16px;
  border: 2px dashed #cbd5e1;
  border-radius: 12px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  transition: all 0.15s;

  &:hover {
    border-color: #4ddbba;
  }
`;

export const StartRecordIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(77, 219, 186, 0.12);
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const StartRecordText = styled.div`
  text-align: center;
`;

export const StartRecordTitle = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #1e293b;
`;

export const StartRecordSubtitle = styled.div`
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
`;

export const LaunchDateValue = styled.span`
  text-align: left;
  flex: 1;
`;

export const LaunchNativeSelect = styled.select`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  color: #344054;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s;
  appearance: auto;
  outline: none;

  &:hover {
    border-color: #98a2b3;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

export const LaunchDateInput = styled.input`
  display: flex;
  align-items: center;
  width: 100%;
  padding: 10px 14px;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
  font-size: 14px;
  color: #344054;
  background: #ffffff;
  cursor: pointer;
  transition: all 0.15s;
  outline: none;

  &:hover {
    border-color: #98a2b3;
  }

  &:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }
`;

export const EmailScheduleSection = styled.div`
  margin-top: 16px;
`;

export const EmailScheduleToggleCard = styled.div`
  padding: 12px 14px;
  border-radius: 8px;
  border: 1px solid #dedede;
  background: #fefefe;

  > div {
    margin-bottom: 0 !important;
  }

  label {
    display: flex !important;
    align-items: center !important;
    margin-bottom: 0 !important;
    cursor: pointer;
    width: 100%;
    gap: 0;
  }

  span {
    font-size: 13px;
    line-height: 16px;
    color: #313131;
  }
`;

export const EmailSendingScheduleAccordion = styled.div`
  margin-bottom: 20px;
`;

export const EmailSendingScheduleTrigger = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 11px 12px 11px 14px;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  background: #f9fafb;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    background-color 0.2s ease,
    box-shadow 0.2s ease;

  .trigger-label {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    font-weight: 500;
    color: #344054;
    line-height: 1.4;
  }

  .trigger-icon {
    width: 15px;
    height: 15px;
    color: #3b82f6;
    flex-shrink: 0;
  }

  .trigger-chevron {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 7px;
    background: #ffffff;
    border: 1px solid #e4e7ec;
    flex-shrink: 0;
    transition: background-color 0.2s ease, border-color 0.2s ease;

    svg {
      width: 14px;
      height: 14px;
      color: #667085;
      transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
      transform: rotate(${props => (props.$expanded ? '90deg' : '0deg')});
    }
  }

  &:hover {
    border-color: #d0d5dd;
    background: #f2f4f7;

    .trigger-chevron {
      border-color: #d0d5dd;
      background: #ffffff;
    }
  }

  ${props =>
    props.$expanded &&
    `
    border-color: #d0d5dd;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);

    .trigger-label {
      color: #101828;
      font-weight: 600;
    }

    .trigger-chevron {
      border-color: #bfdbfe;
      background: #eff6ff;

      svg {
        color: #3b82f6;
      }
    }
  `}
`;

export const EmailSendingScheduleContent = styled.div`
  display: grid;
  grid-template-rows: ${props => (props.$open ? '1fr' : '0fr')};
  transition: grid-template-rows 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  > .inner {
    overflow: hidden;
    min-height: 0;
    opacity: ${props => (props.$open ? 1 : 0)};
    transition: opacity 0.3s ease;
  }

  .fields {
    padding-top: 12px;
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  .email-schedule-fields {
    margin: 0;
  }
`;

export const DrawerOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  z-index: 300;
  opacity: ${props => (props.$open ? 1 : 0)};
  pointer-events: ${props => (props.$open ? 'all' : 'none')};
  transition: opacity 0.25s ease;
`;

export const DrawerPanel = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 480px;
  background: #ffffff;
  box-shadow: -4px 0 24px rgba(0, 0, 0, 0.1);
  transform: ${props => (props.$open ? 'translateX(0)' : 'translateX(100%)')};
  transition: transform 0.3s ease;
  z-index: 301;
  display: flex;
  flex-direction: column;
`;

export const DrawerHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid #eaecf0;
  flex-shrink: 0;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: #1a1a2e;
    margin: 0;
  }

  button {
    background: none;
    border: none;
    cursor: pointer;
    color: #64748b;
    font-size: 20px;
    line-height: 1;
    padding: 4px;
    border-radius: 6px;
    display: flex;
    align-items: center;
    &:hover { background: #f1f5f9; color: #1a1a2e; }
  }
`;

export const DrawerTabs = styled.div`
  display: flex;
  padding: 0 24px;
  border-bottom: 1px solid #eaecf0;
  flex-shrink: 0;
  gap: 4px;
`;

export const DrawerTab = styled.button`
  padding: 10px 14px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  font-weight: ${props => (props.$active ? 600 : 400)};
  color: ${props => (props.$active ? '#3b82f6' : '#64748b')};
  border-bottom: 2px solid ${props => (props.$active ? '#3b82f6' : 'transparent')};
  transition: all 0.15s;
  white-space: nowrap;

  &:hover { color: #3b82f6; }
`;

export const DrawerBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
`;

export const ExecutionRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 12px 24px;
  border-bottom: 1px solid #f1f5f9;
  cursor: default;
  transition: background 0.1s;

  &:hover { background: #f8fafc; }
`;

export const ExecutionRowTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

export const ExecutionLeadName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const ExecutionStatusBadge = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 99px;
  flex-shrink: 0;
  ${props => {
    switch (props.$status) {
      case 'completed': return 'background: #dcfce7; color: #16a34a;';
      case 'failed': return 'background: #fee2e2; color: #dc2626;';
      case 'running': return 'background: #dbeafe; color: #2563eb;';
      case 'pending': return 'background: #f1f5f9; color: #64748b;';
      case 'paused': return 'background: #fef9c3; color: #ca8a04;';
      default: return 'background: #f1f5f9; color: #64748b;';
    }
  }}
`;

export const ExecutionMeta = styled.div`
  display: flex;
  gap: 12px;
  font-size: 11px;
  color: #94a3b8;
`;

export const ExecutionError = styled.div`
  font-size: 11px;
  color: #dc2626;
  background: #fef2f2;
  border-radius: 6px;
  padding: 6px 10px;
  margin-top: 4px;
  word-break: break-word;
`;

export const DrawerFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  border-top: 1px solid #eaecf0;
  flex-shrink: 0;

  span {
    font-size: 12px;
    color: #94a3b8;
  }
`;

export const DrawerPageButton = styled.button`
  padding: 6px 14px;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
  color: #1e293b;
  font-weight: 500;
  transition: all 0.15s;

  &:disabled { opacity: 0.4; cursor: default; }
  &:not(:disabled):hover { background: #f1f5f9; }
`;

export const DrawerEmpty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  font-size: 14px;
  color: #94a3b8;
`;

// ============ TINYMCE GLOBAL OVERRIDE (for modal context) ============
export const TinyMceModalGlobalStyle = createGlobalStyle`
  .tox.tox-tinymce-aux,
  .tox-pop,
  .tox-pop__dialog,
  .tox-menu,
  .tox-collection,
  .tox-collection__group,
  .tox-dialog-wrap,
  .tox-dialog,
  .tox-silver-sink {
    z-index: 9999999 !important;
  }
`;

// ============ EMAIL BODY EDITOR ============
export const EmailBodyWrapper = styled.div`
  position: relative;

  .tox .tox-toolbar__primary{
  background-image:none;
  }
`;

export const EmailBodyExpandBtn = styled.button`
  position: absolute;
  bottom: 6px;
  right: 6px;
  z-index: 10;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  cursor: pointer;
  color: #64748b;
  transition: all 0.15s;

  &:hover {
    background: #f0f4ff;
    border-color: #3b82f6;
    color: #3b82f6;
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const ExpandedEditorBody = styled.div`
  padding: 0 16px 16px;
  .tox-tinymce {
    border-radius: 10px;
  }

  .tox .tox-toolbar__primary{
  background-image:none;
  }

  .tox-tinymce{
  height: 400px !important;
  }
`;

// ============ SEND EMAIL NODE ============
export const EmailAccountSelector = styled.div`
  position: relative;
`;

export const EmailAccountTrigger = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  color: ${props => (props.$hasValue ? '#1e293b' : '#94a3b8')};
  cursor: pointer;
  transition: all 0.15s;
  text-align: left;

  &:hover {
    border-color: #cbd5e1;
  }

  &:focus {
    outline: none;
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08);
  }

  svg {
    width: 16px;
    height: 16px;
    color: #94a3b8;
    flex-shrink: 0;
    transition: transform 0.2s;
    transform: ${props => (props.$open ? 'rotate(180deg)' : 'rotate(0deg)')};
  }
`;

export const EmailAccountDropdown = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  right: 0;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  z-index: 20;
  padding: 6px;
  animation: ${fadeIn} 0.15s ease;
`;

export const EmailAccountOption = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  background: transparent;
  border: none;
  border-radius: 6px;
  cursor: ${props => (props.$static ? 'default' : 'pointer')};
  text-align: left;
  transition: all 0.12s;

  &:hover {
    background: ${props => (props.$static ? 'transparent' : '#f8fafc')};
  }

  .account-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
    min-width: 0;
  }

  .account-label {
    font-size: 13px;
    font-weight: 500;
    color: ${props => {
    if (props.$muted) return '#94a3b8';
    return props.$selected ? '#16a34a' : '#1e293b';
  }};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .account-email {
    font-size: 11px;
    color: #94a3b8;
  }

  .check-icon {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #16a34a;
    opacity: ${props => (props.$selected ? '1' : '0')};
    transition: opacity 0.15s;
  }
`;
